import React, { useState } from 'react';
import {
    Box, Paper, Button, TextField, Select, MenuItem,
    useMediaQuery, InputLabel, Typography, useTheme,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import Header from "../global/Header";
import { useUpdateOrganizationMutation } from "../../../features/organizations/organizationsApiSlice";
import { tokens } from "../../../theme"


function groupByField(changes) {
    const grouped = {};

    Object.entries(changes).forEach(([key, change]) => {
        const field = key.split('-')[1];

        if (!grouped[field]) {
            grouped[field] = [];
        }

        grouped[field].push(change);
    });

    return grouped;
}

const AgencyLdc = ({
    value,
    organization,
    partnerIdFilter,
    setPartnerIdFilter,
    clientNameFilter,
    setClientNameFilter,
    setSelectedCells,
}) => {

    // 1. Maintaining local state to hold changes
    const [rows, setRows] = useState(organization.ldc_clients);
    const [changes, setChanges] = useState({});

    const BooleanEditInput = ({ id, value, api, field }) => {
        const handleChange = (event) => {
            const clientName = api.getRow(id).client_name_cleaned;

            setChanges(prevChanges => ({
                ...prevChanges,
                [`${id}-${field}`]: {
                    value: event.target.value === 'true',
                    clientName: clientName
                }
            }));
            // Create a new array of rows
            const newRows = rows.map(row => {
                if (row.partner_id === id) {
                    // This is the row we're editing. Copy it and change the appropriate field.
                    return { ...row, [field]: event.target.value === 'true' };
                }
                // Not the row we're editing. Return it unchanged.
                return row;
            });

            // Update the rows state
            setRows(newRows);

            api.setEditCellValue({ id, field, value: event.target.value === 'true' }, event);
            api.commitCellChange({ id, field });
            api.setCellMode(id, field, 'view');
        };


        return (
            <Select value={value ? 'true' : 'false'} onChange={handleChange}>
                <MenuItem value={'true'}>True</MenuItem>
                <MenuItem value={'false'}>False</MenuItem>
            </Select>
        );
    };

    const StringEditInput = ({ id, value, api, field }) => {
        const handleChange = (event) => {
            const clientName = api.getRow(id).client_name_cleaned;

            setChanges(prevChanges => ({
                ...prevChanges,
                [`${id}-${field}`]: {
                    value: event.target.value, // Just store the new value
                    clientName: clientName
                }
            }));

            // Create a new array of rows
            const newRows = rows.map(row => {
                if (row.partner_id === id) {
                    // This is the row we're editing. Copy it and change the appropriate field.
                    return { ...row, [field]: event.target.value };
                }
                // Not the row we're editing. Return it unchanged.
                return row;
            });

            // Update the rows state
            setRows(newRows);

            api.setEditCellValue({ id, field, value: event.target.value }, event);
            api.commitCellChange({ id, field });
            api.setCellMode(id, field, 'view');
        };


        return (
            <TextField value={value} onChange={handleChange} />
        );
    };

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);

    // Handle save changes click
    const handleSaveChangesClick = () => {
        setOpenDialog(true);
    };

    // Add a new state to hold the updated clients
    const [updatedClients, setUpdatedClients] = useState(organization.ldc_clients);


    // Handle confirm changes
    const handleConfirmChanges = async () => {
        // Convert changes object to array of updated clients
        const newUpdatedClients = Object.entries(changes).map(([key, value]) => {
            const [id, field] = key.split('-');
            const client = organization.ldc_clients.find(c => c.partner_id === id);
            return { ...client, [field]: value };
        });

        console.log("Updated Clients: ", newUpdatedClients)
        console.log("Changes: ", changes)
        console.log("Clients: ", updatedClients)


        // Merge updated clients with the original list
        const mergedClients = updatedClients.map(client => {
            // Check if this client has been updated
            const updatedClient = newUpdatedClients.find(updated => updated.partner_id === client.partner_id);

            // If it has, use the updated version, else use the original
            return updatedClient || client;
        });

        setUpdatedClients(mergedClients);
        console.log("Merged Clients: ", mergedClients)

        try {
            await updateOrganization({
                id: organization._id,
                ldc_clients: mergedClients,
                name: organization.name,
                address: organization.address,
                phone: organization.phone,
                s3_uri: organization.s3_uri,
                storage_integration: organization.storage_integration,
                pid: organization.pid,
                adc_enabled: organization.adc_enabled,
                ldc_enabled: organization.ldc_enabled,
                auction_enabled: organization.auction_enabled,
                beeswax_cookie_sync: organization.beeswax_cookie_sync,
                bito_hem_enabled: organization.bito_hem_enabled,
                email: organization.email,
                delivery_format: organization.delivery_format,
                aws_account_id: organization.aws_account_id,
            });
        } catch (err) {
            // handle error if the request fails
            console.error("Failed to update organization", err);
        }

        // Close the dialog
        setOpenDialog(false);

        // Reset the changes
        setChanges({});
    };

    // Handle cancel changes
    const handleCancelChanges = () => {
        // Close the dialog
        setOpenDialog(false);

        // Reset the changes
        setChanges({});
    };



    const [updateOrganization, {
        isOrgLoading,
        isOrgSuccess,
        isOrgError,
        orgError
    }] = useUpdateOrganizationMutation()


    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const ldcClientColumns = [
        {
            field: 'partner_id',
            headerName: 'Partner ID',
            width: 100,
            filterable: true,
            editable: true,
            renderEditCell: StringEditInput
        },
        {
            field: 'name',
            headerName: 'Client Name',
            width: 175,
            editable: true,
            renderEditCell: StringEditInput
        },
        {
            field: 'client_name_cleaned',
            headerName: 'Cleaned Name',
            width: 150,
            editable: true,
            renderEditCell: StringEditInput
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'ip_company',
            headerName: 'IP Company',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'b2b_resolution',
            headerName: 'B2B Resolution',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'b2c_resolution',
            headerName: 'B2C Resolution',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'pte_resolution',
            headerName: 'PTE Resolution',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 's3_delivery',
            headerName: 'S3 Delivery',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'file_aggregate',
            headerName: 'File Aggregate',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'aggregate_bucket',
            headerName: 'Aggregate Bucket',
            width: 150,
            editable: true,
            renderEditCell: StringEditInput
        },
        {
            field: 'row_level_aggregation',
            headerName: 'Row Level Aggregation',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'json_headers',
            headerName: 'JSON Headers',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 'raw_hems',
            headerName: 'Raw HEMS',
            width: 75,
            editable: true,
            type: 'boolean',
            renderCell: (params) => (
                <span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.5em',
                            color: params.value ? 'green' : 'red',
                        }}
                    >
                        {params.value ? '✓' : '✗'}
                    </span>
                    {params.value ? ' True' : ' False'}
                </span>
            ),
            renderEditCell: BooleanEditInput,
        },
        {
            field: 's3_uri',
            headerName: 'S3 URI',
            width: 150,
            editable: true,
            renderEditCell: StringEditInput
        },
        {
            field: 'storage_integration',
            headerName: 'Storage Integration',
            width: 120,
            editable: true,
            renderEditCell: StringEditInput
        },
    ];
    return (
        <Box m="20px">
            <Header title="LDC Info" />
            <Box
                m="40px 0 0 0"
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.blueAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.blueAccent[200]} !important`,
                    },
                }}
            >
                {organization.ldc_clients &&
                    <Box>
                        <Box sx={{ display: 'flex', gap: '20px' }}>
                            <TextField
                                value={partnerIdFilter}
                                onChange={(event) => setPartnerIdFilter(event.target.value)}
                                placeholder="Search by Partner ID"
                            />
                            <TextField
                                value={clientNameFilter}
                                onChange={(event) => setClientNameFilter(event.target.value)}
                                placeholder="Search by Client Name"
                            />
                            <Button onClick={handleSaveChangesClick} variant="contained" color="primary">
                                Save Changes
                            </Button>
                            <Dialog open={openDialog} onClose={handleCancelChanges}>
                                <DialogTitle>Confirm Changes</DialogTitle>
                                <DialogContent>
                                    {Object.entries(groupByField(changes)).map(([field, changes]) => (
                                        <Paper elevation={3} style={{ margin: '10px 0', padding: '15px' }}>
                                            <Typography variant="h6">{field}</Typography>
                                            {changes.map(({ clientName, value }) => (
                                                <div key={`${clientName}-${field}`}>
                                                    {`Client ${clientName} changed to ${value}`}
                                                </div>
                                            ))}
                                        </Paper>
                                    ))}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleConfirmChanges} color="primary">
                                        Confirm
                                    </Button>
                                    <Button onClick={handleCancelChanges} color="secondary">
                                        Cancel
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>

                        <DataGridPro
                            rows={rows.filter((row) => {
                                return (
                                    row.partner_id.toLowerCase().includes(partnerIdFilter.toLowerCase()) &&
                                    row.name.toLowerCase().includes(clientNameFilter.toLowerCase())
                                );
                            })}
                            columns={ldcClientColumns}
                            onCellClick={(params, event) => {
                                if (event.ctrlKey || event.metaKey) {
                                    setSelectedCells(prevSelectedCells => [
                                        ...prevSelectedCells,
                                        { rowId: params.id, field: params.field }
                                    ]);
                                } else {
                                    setSelectedCells([{ rowId: params.id, field: params.field }]);
                                }
                            }}
                            getRowId={(row) => row.partner_id}
                            components={{
                                Toolbar: GridToolbar,
                            }}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    border: '.5px solid black',
                                },
                            }}
                            onEditCellChangeCommitted={(params) => {
                                const newRows = [...rows];
                                const updatedRow = newRows.find((row) => row.partner_id === params.id);

                                if (updatedRow) {
                                    updatedRow[params.field] = params.props.value;
                                    setRows(newRows);
                                }
                            }}
                        />
                    </Box>
                }
            </Box>
        </Box>
    );
};

export default AgencyLdc;